import React from 'react';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { ButtonDemo, TabState } from '../../../../constants/settings-events';
import { withClientSideRenderMark } from '../../../../contexts/ClientSideRenderMark';
import { useOpenCartAfterScroll } from '../../../../hooks';
import { useShouldUseMobileSettings } from '../../../../hooks/useShouldUseMobileSettings';
import { ListProps } from '../../../../types/common';
import { Toast } from '../Toast';
import { Disclaimer } from './Disclaimer';
import { EmptyState, EmptyStateProps } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Header } from './Header';
import { st, classes } from './List.st.css';
import { getVisibleVariants, PlanCardsWithVariants } from './PlanCardsWithVariants';
import { PlanList, MAX_ITEM_WIDTH } from './PlanList';

export interface ListWidgetProps extends ListProps {
  tabState: TabState;
  buttonDemo: ButtonDemo;
  areSettingsOpened: boolean;
  dataHook?: string;
  emptyState: EmptyStateProps;
  locale?: string;
}

const List: React.FC<ListWidgetProps> = ({
  plans,
  variantEntries,
  variantCategoryNameById,
  isVariantSelectionEnabled,
  plansErrorMessage,
  retryLoadPlans,
  isRetryLoading,
  tabState,
  buttonDemo,
  areSettingsOpened,
  selectPlan,
  addToCart,
  resetLoadingState,
  dataHook,
  hideToast,
  emptyState,
  selectedPlanId,
  overrideAppTitle,
  overrideAppSubtitle,
  toast,
  isOnEcom,
  isAddToCartLoading,
  scrollToComp,
}) => {
  const { isMobile, isEditorX } = useEnvironment();

  const useMobileSettings = useShouldUseMobileSettings();
  const { experiments } = useExperiments();
  const isVariantsInPlanCardEnabled = experiments.enabled(TPA_EXPERIMENTS.ENABLE_VARIANTS_IN_PLAN_CARD);

  const extraPlanListPadding = 30;
  const variantEntryCount = Object.values(variantEntries ?? {}).flat().length;
  const planListEntryCount = isVariantSelectionEnabled && variantEntryCount > 0 ? variantEntryCount : plans.length;
  const entryCount = isVariantsInPlanCardEnabled
    ? plans.filter((plan) => getVisibleVariants({ plan, variantEntries }).length).length
    : planListEntryCount;
  const maxWidthStyle =
    !isEditorX && entryCount ? { maxWidth: MAX_ITEM_WIDTH * entryCount - extraPlanListPadding } : {};

  useOpenCartAfterScroll(scrollToComp?.shouldOpenCart, scrollToComp?.openSideCart);

  if (plansErrorMessage) {
    return <ErrorState onRetry={retryLoadPlans} isLoading={isRetryLoading} />;
  }

  if (entryCount === 0) {
    return <EmptyState {...emptyState} />;
  }

  return (
    <div
      className={st(classes.root, { mobile: !!isMobile, useMobileSettings })}
      data-hook={dataHook ?? VIEWER_HOOKS.PACKAGE_PICKER_WRAPPER}
    >
      <Header
        overrideAppTitle={overrideAppTitle}
        overrideAppSubtitle={overrideAppSubtitle}
        maxWidthStyle={maxWidthStyle}
      />
      <StatusToast hideToast={hideToast} toast={toast} />
      {isVariantsInPlanCardEnabled ? (
        <PlanCardsWithVariants
          plans={plans}
          variantEntries={variantEntries}
          variantCategoryNameById={variantCategoryNameById ?? {}}
          isVariantSelectionEnabled={isVariantSelectionEnabled}
          showDemoHighlight={tabState === TabState.HIGHLIGHTED}
          buttonDemo={buttonDemo}
          expandMobileBenefits={areSettingsOpened}
          selectPlan={selectPlan}
          addToCart={addToCart}
          resetLoadingState={resetLoadingState}
          selectedPlanId={selectedPlanId}
          isOnEcom={isOnEcom}
          isAddToCartLoading={isAddToCartLoading}
        />
      ) : (
        <PlanList
          selectPlan={selectPlan}
          addToCart={addToCart}
          resetLoadingState={resetLoadingState}
          plans={plans}
          variantEntries={variantEntries}
          variantCategoryNameById={variantCategoryNameById}
          isVariantSelectionEnabled={isVariantSelectionEnabled}
          buttonDemo={buttonDemo}
          showDemoHighlight={tabState === TabState.HIGHLIGHTED}
          expandMobileBenefits={areSettingsOpened}
          selectedPlanId={selectedPlanId}
          isOnEcom={isOnEcom}
          isAddToCartLoading={isAddToCartLoading}
        />
      )}
      <Disclaimer maxWidthStyle={maxWidthStyle} />
    </div>
  );
};

type StatusToastProps = Pick<ListWidgetProps, 'toast' | 'hideToast'>;

const StatusToast = ({ toast, hideToast }: StatusToastProps) => {
  if (toast) {
    return (
      <Toast onClose={hideToast} informational={toast.informational}>
        {toast.message}
      </Toast>
    );
  }

  return null;
};

export default withClientSideRenderMark(List);
